// Libraries
import { SystemStyleObject } from '@chakra-ui/react';
import { ChakraStylesConfig, GroupBase, OptionProps } from 'chakra-react-select';
// Types
import { Option } from './Dropdown.types';

/**
 * Function to customize the style of the option component in a react-select dropdown.
 *
 * @function
 * @typeParam OptionType - The type of the option data in the select menu.
 * @typeParam IsMulti - Boolean type indicating whether the select is multi.
 *
 * @param provided - The default styles of the react-select option component.
 * @param state - The current state of the option component.
 *
 * @returns The modified styles for the option component.
 */
const option = <OptionType, IsMulti extends boolean>(
  provided: SystemStyleObject,
  state: OptionProps<OptionType, IsMulti, GroupBase<OptionType>>
) => {
  const bg = state.isFocused ? 'gray.100' : 'transparent';
  const fontWeight = state.isSelected ? 'semibold' : 'normal';
  return {
    ...provided,
    bg,
    pl: 3,
    fontWeight,
    fontSize: 'm',
    color: 'text.base',
    _active: {
      bg: 'gray.200',
    },
    _selected: {
      bg,
    },
  };
};

/**
 * Function to customize the style of the menu list in a react-select dropdown.
 *
 * @function
 * @param provided - The default styles of the react-select menu list.
 *
 * @returns The modified styles for the menu list.
 */
const menuList = (provided: SystemStyleObject) => ({
  ...provided,
  bg: 'white',
  pt: 0,
  pl: 0,
  ml: '-1px',
  width: 'calc(100% + 2px)',
  border: 'greyCheck',
  borderRadius: 'md',
  boxShadow: 'dropdown',
  borderTop: 'none',
  borderTopLeftRadius: 'none',
  borderTopRightRadius: 'none',
});

/**
 * Function to customize the style of the control component in a react-select dropdown.
 *
 * @function
 * @param provided - The default styles of the react-select control component.
 *
 * @returns The modified styles for the control component.
 */
const control = (provided: SystemStyleObject) => ({
  ...provided,
  my: 2.5,
  mx: 3,
  w: 'auto',
  minH: 8,
  p: '0',
  borderRadius: 'md',
});

/**
 * Function to generate a configuration object containing the styles for a react-select dropdown menu.
 *
 * @function
 * @typeParam OptionType - The type of the option data in the select menu.
 * @typeParam IsMulti - Boolean type indicating whether the select is multi.
 *
 * @returns A configuration object containing the styles for the option, menu list, and control components of a react-select dropdown menu.
 */
export const selectStyles = <OptionType extends Option, IsMulti extends boolean>(): ChakraStylesConfig<
  OptionType,
  IsMulti,
  GroupBase<OptionType>
> => ({
  option,
  menuList,
  control,
});
